import {
  SessionDisplay,
  SessionModel,
  SessionStayPeriod,
} from '../session/model';
import { Observable, combineLatest, of } from 'rxjs';
import {
  createRoomDescription,
  createRoomSearch,
  RoomDescriptionModel,
  RoomHelper,
  RoomModel,
  RoomSearchModel,
  XHRRoomSearchModel,
} from './model';
import { Http } from '../../core/http';
import { RoomApi } from './room.api';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SessionService } from '../session';
import { MoneyPrice } from '../../core/money';
import { PricesForStayPeriod } from './room.service';
import { Env } from '../../env';

const fetchGetRoomsSearch = (sessionId: SessionModel['sessionId']) => {
  const headers = {
    'X-AVP-Properties': 'Description',
    'X-AVP-PictureTemplates': 'Original|XLarge',
    'X-AVP-DescriptionType': 'text/plain',
  };

  return Http.get<any>(RoomApi.getSearch(sessionId), {
    headers,
  }).pipe(
    map((response) => {
      return {
        ...response,
        rooms: response.rooms.map((room: RoomSearchModel) =>
          createRoomSearch(room)
        ),
      };
    })
  );
};

const fetchGetRoomDescription = (
  sessionId: SessionModel['sessionId'],
  roomId: RoomModel['roomId']
) => {
  return Http.get<any>(RoomApi.getRoomDescription(sessionId, roomId)).pipe(
    map((roomResponse) => roomResponse)
  );
};

const fetchGetRoomsDescription = (sessionId: SessionModel['sessionId']) => {
  return Http.get<any>(RoomApi.getDescription(sessionId)).pipe(
    map((response) =>
      response.rooms.map((room: RoomDescriptionModel) =>
        createRoomDescription(room)
      )
    )
  );
};

const getRoomsSearch = (
  sessionId: SessionModel['sessionId']
): Observable<XHRRoomSearchModel> => {
  return fetchGetRoomsSearch(sessionId).pipe(
    catchError(SessionService.shouldBeSessionError)
  );
};

const getRoomDescription = (
  sessionId: SessionModel['sessionId'],
  roomId: RoomModel['roomId']
) => {
  return fetchGetRoomDescription(sessionId, roomId)?.pipe(
    //TODO CHECK FIX
    catchError(SessionService.shouldBeSessionError)
  );
};

const getRoomsDescription = (
  sessionId: SessionModel['sessionId']
): Observable<RoomDescriptionModel[]> => {
  return fetchGetRoomsDescription(sessionId).pipe(
    catchError(SessionService.shouldBeSessionError)
  );
};

const fetchRoom = (
  sessionId: SessionModel['sessionId'],
  roomId: RoomModel['roomId']
) => {
  return getRoomDescription(sessionId, roomId);
};

const fetchRooms = (sessionId: SessionModel['sessionId']) => {
  return combineLatest([
    getRoomsDescription(sessionId),
    getRoomsSearch(sessionId),
  ]);
};

const getRoomsSearchForSpecificStayPeriod = (
  stayPeriod: SessionStayPeriod,
  currency: SessionDisplay['currency']
) => {
  return SessionService.fetchCreateSession(
    {
      context: {
        stayPeriod,
        guests: {
          adults: Env.DefaultNumberOfAdults,
          children: Env.DefaultNumberOfChildren,
          infants: Env.DefaultNumberOfInfants,
        },
        selectedHotelId: Env.HotelId,
      }, //TODO CHECK FIX
      display: {
        currency,
        countryCode: Env.Country,
        culture: Env.DefaultLanguage,
      },
    },
    { preventShare: true }
  ).pipe(
    switchMap((createdSession) =>
      SessionService.fetchGetSession(createdSession.sessionId, {
        preventShare: true,
      })
    ),
    switchMap((getSession) =>
      RoomHelperService.getRoomsSearch(getSession.sessionId)
    ),
    map((res) =>
      res.rooms.reduce((acc, room) => {
        return { ...acc, [room.roomId]: RoomHelper.getCheapestPrice(room) };
      }, {} as { [roomId: string]: MoneyPrice })
    ),
    catchError((err) => {
      console.log(err);
      return of({});
    })
  );
};

const formatPricesForStayPeriods = (
  stayPeriods: SessionStayPeriod[],
  prices: Array<{ [roomId: string]: MoneyPrice }>
): PricesForStayPeriod => {
  const formattedObjectByPeriods = stayPeriods.reduce(
    (acc, stayPeriod, index) => {
      return { ...acc, [`${stayPeriod.arrivalDate}`]: prices[index] };
    },
    {}
  );
  const pricesLength = prices.map((p) => Object.keys(p).length);
  const indexMaxPricesLength = pricesLength.indexOf(Math.max(...pricesLength));

  return Object.keys(prices[indexMaxPricesLength]).reduce((acc, roomId) => {
    const pricesByPeriod = Object.keys(formattedObjectByPeriods).reduce(
      (obj, period) => ({
        ...obj,
        [period as string]:
          formattedObjectByPeriods[
            period as keyof typeof formattedObjectByPeriods
          ][roomId],
      }),
      {}
    ); //TODO CHECK FIX
    return { ...acc, [roomId]: pricesByPeriod };
  }, {});
};

export const RoomHelperService = {
  getRoomsSearch,
  getRoomsDescription,
  getRoomsSearchForSpecificStayPeriod,
  formatPricesForStayPeriods,
  fetchRoom,
  fetchRooms,
};
